import React, { useState, useCallback } from "react";
import { clsx } from "clsx";
import {
    OutlinedButton,
    RecentTimestamp,
    RelativeDateFormat,
} from "@bloomberg/media-ui-core";

import { fetchAuthorFromApi } from "~api/client/fetchAuthorFromApi";
import integrityCheck from "~components/modules/story-list/variants/author-page/StoryListAuthorPage.integrity";
import Brow from "~components/shared/brow/Brow";
import Headline from "~components/shared/headline/Headline";
import ItemWithTracking from "~components/shared/item-with-tracking/ItemWithTracking";
import LazyImage from "~components/shared/lazy-image/LazyImage";
import Module from "~components/shared/module/Module";
import Summary from "~components/shared/summary/Summary";
import Title from "~components/shared/title/Title";
import { DEFAULT_PAGE_NUMBER } from "~constants/author";
import type { CuratedStory, Overwrite, VariationComponent } from "~types/types";
import { integrityError } from "~utils/moduleIntegrity";

import styles from "./StoryListAuthorPage.module.css";

const DEFAULT_IMAGE_WIDTH = 197;
const DEFAULT_IMAGE_HEIGHT = 131;
const DEFAULT_IMAGE_ASPECT_RATIO = 197 / 131;

export interface AuthorProps {
    authorSlug: string;
    authorType: string;
}

export type StoryListAuthorPageProps = Pick<
    Overwrite<VariationComponent, { items: CuratedStory[] }>,
    | "abbaConfigKey"
    | "componentName"
    | "id"
    | "items"
    | "renderingLocation"
    | "title"
    | "tracking"
    | "duplicateOf"
> &
    AuthorProps;

export default function StoryListAuthorPage({
    abbaConfigKey,
    componentName = "StoryListAuthorPage",
    duplicateOf,
    id,
    items = [],
    renderingLocation,
    title,
    tracking,
    authorSlug,
}: StoryListAuthorPageProps) {
    const [stories, setStories] = useState(items);
    const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE_NUMBER);
    const [paginationLoading, setPaginationLoading] = useState(false);

    const fetchMoreStories = useCallback(async () => {
        setPaginationLoading(true);

        const paginatedAuthor = await fetchAuthorFromApi({
            authorSlug,
            pageNumber: currentPage + 1,
        });
        const paginatedStories =
            (paginatedAuthor?.items as CuratedStory[]) || [];

        setStories([...stories, ...paginatedStories]);
        setCurrentPage(currentPage + 1);
        setPaginationLoading(false);
    }, [currentPage, paginationLoading]);

    const renderingClasses = clsx(
        styles[renderingLocation],
        styles[componentName]
    );

    const integrity = integrityCheck({ items });
    if (integrity.length) return integrityError(integrity);

    const storyCount = stories?.length;

    return (
        <Module
            abbaConfigKey={abbaConfigKey}
            className={renderingClasses}
            data-component="story-list-author-page"
            duplicateOf={duplicateOf}
            id={id}
        >
            <Title title={title} />

            {stories.map((item) => (
                <ItemWithTracking
                    key={item.id}
                    className={styles.article}
                    moduleTracking={tracking}
                    storyCount={storyCount}
                    item={item}
                    containerTag="article"
                >
                    {item.image && (
                        <a className={styles.image} href={item.url}>
                            <LazyImage
                                image={item.image}
                                altFallback={item.headline}
                                contentType={item.type}
                                width={DEFAULT_IMAGE_WIDTH}
                                height={DEFAULT_IMAGE_HEIGHT}
                                aspectRatio={DEFAULT_IMAGE_ASPECT_RATIO}
                            />
                        </a>
                    )}
                    <div className={styles.info}>
                        <Brow eyebrow={item.eyebrow} credits={item.credits} />
                        <Headline text={item.headline} url={item.url} />
                        <Summary summary={item.summary} />
                        <RecentTimestamp
                            publishedAt={item.publishedAt}
                            updatedAt={item.updatedAt}
                            format={RelativeDateFormat.LONG}
                        />
                    </div>
                </ItemWithTracking>
            ))}
            <OutlinedButton
                title="more stories"
                onClick={fetchMoreStories}
                loading={paginationLoading}
            >
                Load More Stories
            </OutlinedButton>
        </Module>
    );
}
